import { Typography, TextField, withStyles } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'src/app/i18n';
import useStyles from './styles/footerStyles';
import useLayoutStyles from './styles/baseLayoutStyles';

import FacebookIcon from 'src/assets/icons/common/FacebookIcon';
import LinkedInIcon from 'src/assets/icons/common/LinkedInIcon';
import { MainButton } from '../buttons/MainButton';
import InstaIcon from 'src/assets/icons/common/InstaIcon';
import { capitalize } from 'src/utils/helpers/common';
import { links, MAILTO } from 'next-seo.config';
import Link from 'next/link';
import { forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { setFooterEmail } from 'src/store/reducer';

const CssTextField = withStyles({
    root: {
        flex: 1,
        '& label.Mui-focused': {
            color: 'white'
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: 'transparent'
        }
    },
    input: {
        backgroundColor: '#F00',
        width: '15ch',
        '&:focus': {
            backgroundColor: '#F00',
            width: '30ch'
        }
    }
})(TextField);

const FooterTypography = withStyles((theme) => ({
    root: {
        gridColumnEnd: 'span 4',
        marginBottom: theme.spacing(2),
        color: '#FFF',
        [theme.breakpoints.down('xs')]: {
            gridColumnEnd: 'span 1',
            gridRowEnd: 'span 1'
        }
    }
}))((props: any) => <Typography variant="body1" {...props} />);

const AboutTypography = withStyles((theme) => ({
    root: {
        gridColumnEnd: 'span 1',
        color: '#FFF',
        alignSelf: 'start',
        gridRowEnd: 'span 1',

        [theme.breakpoints.down('sm')]: {
            gridColumnEnd: 'span 2'
        }
    }
}))((props: any) => <Typography variant="body2" {...props} />);

const GrayTypography = withStyles((theme) => ({
    root: {
        marginBottom: theme.spacing(6),
        // fontWeight: 300,
        color: theme.palette.grey['300']
    }
}))((props: any) => <Typography variant="body2" {...props} />);

const GreenTypography = withStyles((theme) => ({
    root: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(6),
        justifySelf: 'center',
        gridColumnEnd: 'span 4',
        [theme.breakpoints.down('xs')]: {
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(4),
            justifySelf: 'start'
        }
    }
}))((props: any) => <Typography variant="h6" {...props} />);

const LeftGreen = withStyles({
    root: { justifySelf: 'start' }
})(GreenTypography);

interface Props {
    variant: AppBackVariant;
}

const titlePages = [
    { title: 'apsy.apsy', href: '/' },
    { title: 'about.aTeam', href: '/a-team' },
    { title: 'about.value', href: '/vision-values' },
    { title: 'about.blog', href: '/blog' }
];

const Footer = forwardRef(({ variant }: Props, ref) => {
    const classes = useStyles();
    const layoutClasses = useLayoutStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation('common');
    return (
        <div
            ref={ref as any}
            id="APSY-Footer"
            className={clsx(classes.root, {
                [layoutClasses.blackBackground]: variant === 'black',
                [layoutClasses.darkBackground]: variant === 'dark',
                [layoutClasses.monoliteBackground]: variant === 'monolite',
                [layoutClasses.lightBackground]: variant === 'light'
            })}>
            <div className={classes.contentContainer}>
                <div className={classes.formContainer}>
                    <FooterTypography className={classes.formLabel}>
                        {t('form.requestConsultation')}
                    </FooterTypography>
                    <div className={classes.emailField}>
                        <CssTextField
                            onChange={(e) => dispatch(setFooterEmail(e.target.value))}
                            placeholder={t('form.enterEmail')}
                            className={classes.textInput}
                        />
                        <Link href="/schedule">
                            <MainButton
                                className={classes.formButton}
                                textProp={{ variant: 'body1', className: classes.formButtonText }}>
                                {t('action.getStarted')}
                            </MainButton>
                        </Link>
                    </div>
                </div>
                <div className={classes.navContainer}>
                    <div className={clsx(classes.sideNav)}>
                        <LeftGreen>{capitalize(t(`nav.contact`))}</LeftGreen>
                        <a href={MAILTO}>
                            <FooterTypography color="textPrimary">
                                {t('apsy.supportEmail')}
                            </FooterTypography>
                        </a>

                        <FooterTypography color="textPrimary">
                            Made with <Typography component="span">♥ </Typography>
                            in LA
                        </FooterTypography>
                    </div>
                    <div className={clsx(classes.middleNav)}>
                        <GreenTypography>{capitalize(t(`nav.about`))}</GreenTypography>

                        {titlePages.map(({ title, href }) => (
                            <Link href={href} key={title}>
                                <a className={classes.nonbreakText}>
                                    <AboutTypography color="textPrimary" key={title}>
                                        {t(`${title}`)}
                                    </AboutTypography>
                                </a>
                            </Link>
                        ))}
                    </div>
                    <div className={clsx(classes.sideNav, classes.rightSideNav)}>
                        <LeftGreen>{capitalize(t(`nav.follow`))}</LeftGreen>
                        <div className={classes.followus}>
                            <a href={links.linkedin} className={classes.iconLink}>
                                <LinkedInIcon className={classes.icon} />
                            </a>
                            <a href={links.instagram} className={classes.iconLink}>
                                <InstaIcon className={classes.icon} />
                            </a>
                            <a href={links.facebook} className={classes.iconLink}>
                                <FacebookIcon className={classes.icon} />
                            </a>
                        </div>
                    </div>
                </div>
                <div className={classes.spacer} />
                <Link href="/legal">
                    <a>
                        <FooterTypography
                            color="textPrimary"
                            className={classes.regularMarginBottom}>{`${t(`form.privacy`)} & ${t(
                            `form.agreement`
                        )}`}</FooterTypography>
                    </a>
                </Link>

                <GrayTypography color="textPrimary">{t(`apsy.cr`)}</GrayTypography>
                <div className={classes.spacer} />
            </div>
        </div>
    );
});

export default Footer;
